import type { SupabaseClient } from "@supabase/supabase-js";

import { isMissingChannelSurface } from "./channels";

export const LIST_MY_UNREAD_CHANNELS_RPC = "list_my_unread_channels";
export const GET_MY_CHANNEL_UNREAD_SUMMARY_RPC = "get_my_channel_unread_summary";
export const MARK_CHANNEL_READ_RPC = "mark_channel_read";

const CHANNEL_UNREAD_LIST_LIMIT = 100;

export const CHANNEL_UNREAD_UNAVAILABLE =
  "Board unread is not available on this server.";
export const CHANNEL_UNREAD_LOAD_FAILED = "Could not load board unread state.";

export interface ChannelUnreadRow {
  readonly channelId: string;
  readonly latestMessageId: string;
  readonly latestMessageAt: string;
  readonly lastOpenedAt: string | null;
  readonly unreadMessageCount: number;
}

export interface ChannelUnreadSummary {
  readonly unreadChannelCount: number;
  readonly unreadMessageCount: number;
  readonly hasUnread: boolean;
}

export type ChannelUnreadListLoad =
  | { readonly status: "ready"; readonly channels: readonly ChannelUnreadRow[] }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

export type ChannelUnreadSummaryLoad =
  | { readonly status: "ready"; readonly summary: ChannelUnreadSummary }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

export type MarkChannelReadOutcome =
  | { readonly ok: true }
  | { readonly ok: false };

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

/** PostgREST may expose bigint columns as either JSON numbers or decimal strings. */
function asNonnegativeCount(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }
  if (typeof value !== "string" || !/^\d+$/.test(value)) return null;
  try {
    const count = BigInt(value);
    return count > BigInt(Number.MAX_SAFE_INTEGER) ? Number.MAX_SAFE_INTEGER : Number(count);
  } catch {
    return null;
  }
}

export function mapChannelUnreadRow(raw: unknown): ChannelUnreadRow | null {
  const row = asRecord(raw);
  if (row === null) return null;
  const channelId = asText(row.channel_id);
  const latestMessageId = asText(row.latest_message_id);
  const latestMessageAt = asText(row.latest_message_at);
  const unreadMessageCount = asNonnegativeCount(row.unread_message_count);
  if (
    channelId === null ||
    latestMessageId === null ||
    latestMessageAt === null ||
    unreadMessageCount === null ||
    unreadMessageCount === 0
  ) {
    return null;
  }
  return {
    channelId,
    latestMessageId,
    latestMessageAt,
    lastOpenedAt: asText(row.last_opened_at),
    unreadMessageCount,
  };
}

export function mapChannelUnreadRows(raw: unknown): ChannelUnreadRow[] | null {
  if (!Array.isArray(raw)) return null;
  const rows: ChannelUnreadRow[] = [];
  const seen = new Set<string>();
  for (const value of raw) {
    const row = mapChannelUnreadRow(value);
    if (row === null || seen.has(row.channelId)) continue;
    seen.add(row.channelId);
    rows.push(row);
  }
  return rows;
}

export function mapChannelUnreadSummary(raw: unknown): ChannelUnreadSummary | null {
  if (!Array.isArray(raw) || raw.length !== 1) return null;
  const row = asRecord(raw[0]);
  if (row === null) return null;
  const unreadChannelCount = asNonnegativeCount(row.unread_channel_count);
  const unreadMessageCount = asNonnegativeCount(row.unread_message_count);
  if (
    unreadChannelCount === null ||
    unreadMessageCount === null ||
    typeof row.has_unread !== "boolean" ||
    row.has_unread !== (unreadChannelCount > 0 && unreadMessageCount > 0)
  ) {
    return null;
  }
  return {
    unreadChannelCount,
    unreadMessageCount,
    hasUnread: row.has_unread,
  };
}

export function hasBoardUnread(load: ChannelUnreadSummaryLoad | null): boolean {
  return load?.status === "ready" && load.summary.hasUnread;
}

export function channelUnreadIds(load: ChannelUnreadListLoad | null): ReadonlySet<string> {
  if (load?.status !== "ready") return new Set<string>();
  return new Set(load.channels.map((row) => row.channelId));
}

export async function fetchChannelUnreadSummary(
  client: SupabaseClient,
  accountId: string,
): Promise<ChannelUnreadSummaryLoad> {
  if (accountId.trim().length === 0) {
    return { status: "failed", reason: CHANNEL_UNREAD_LOAD_FAILED };
  }
  try {
    const { data, error } = await client.rpc(GET_MY_CHANNEL_UNREAD_SUMMARY_RPC, {
      p_account_id: accountId,
    });
    if (error !== null) {
      return isMissingChannelSurface(error)
        ? { status: "unavailable", reason: CHANNEL_UNREAD_UNAVAILABLE }
        : { status: "failed", reason: CHANNEL_UNREAD_LOAD_FAILED };
    }
    const summary = mapChannelUnreadSummary(data);
    return summary === null
      ? { status: "failed", reason: CHANNEL_UNREAD_LOAD_FAILED }
      : { status: "ready", summary };
  } catch {
    return { status: "failed", reason: CHANNEL_UNREAD_LOAD_FAILED };
  }
}

export async function fetchUnreadChannels(
  client: SupabaseClient,
  accountId: string,
): Promise<ChannelUnreadListLoad> {
  if (accountId.trim().length === 0) {
    return { status: "failed", reason: CHANNEL_UNREAD_LOAD_FAILED };
  }
  try {
    const { data, error } = await client.rpc(LIST_MY_UNREAD_CHANNELS_RPC, {
      p_account_id: accountId,
      p_limit: CHANNEL_UNREAD_LIST_LIMIT,
    });
    if (error !== null) {
      return isMissingChannelSurface(error)
        ? { status: "unavailable", reason: CHANNEL_UNREAD_UNAVAILABLE }
        : { status: "failed", reason: CHANNEL_UNREAD_LOAD_FAILED };
    }
    const channels = mapChannelUnreadRows(data);
    return channels === null
      ? { status: "failed", reason: CHANNEL_UNREAD_LOAD_FAILED }
      : { status: "ready", channels };
  } catch {
    return { status: "failed", reason: CHANNEL_UNREAD_LOAD_FAILED };
  }
}

export async function markChannelReadCursor(
  client: SupabaseClient,
  accountId: string,
  channelId: string,
  newestRenderedMessageId: string | null,
): Promise<MarkChannelReadOutcome> {
  const trimmedAccountId = accountId.trim();
  const trimmedChannelId = channelId.trim();
  const trimmedMessageId = newestRenderedMessageId?.trim() ?? null;
  if (
    trimmedAccountId.length === 0 ||
    trimmedChannelId.length === 0 ||
    (newestRenderedMessageId !== null && trimmedMessageId?.length === 0)
  ) {
    return { ok: false };
  }
  try {
    const { error } = await client.rpc(MARK_CHANNEL_READ_RPC, {
      p_account_id: trimmedAccountId,
      p_channel_id: trimmedChannelId,
      p_through_message_id: trimmedMessageId,
    });
    return error === null ? { ok: true } : { ok: false };
  } catch {
    return { ok: false };
  }
}
